export type AuthingSession = {
	avatar?: string;
	email?: string;
	id: string;
	name: string;
	nickname?: string;
	phone?: string;
	token: string;
	username?: string;
};

export type AuthingUserLike = {
	access_token?: string | null;
	email?: string | null;
	id?: string | null;
	id_token?: string | null;
	name?: string | null;
	nickname?: string | null;
	phone_number?: string | null;
	phone?: string | null;
	photo?: string | null;
	picture?: string | null;
	preferred_username?: string | null;
	sub?: string | null;
	token?: string | null;
	username?: string | null;
};

const PRIVATE_DISPLAY_NAME_PATTERN = /^(\+?\d[\d\s-]{6,}\d)$/;
const trimValue = (value?: string | null) => value?.trim() || "";
const maskPrivateDisplayName = (value: string) => {
	if (PRIVATE_DISPLAY_NAME_PATTERN.test(value)) {
		const digits = value.replace(/\D/gu, "");
		if (digits.length >= 8) return `${digits.slice(0, 3)}****${digits.slice(-4)}`;
	}
	if (value.includes("@")) {
		const [name] = value.split("@");
		return name || value;
	}
	return value;
};
const DEFAULT_AUTHING_APP_ID = "6a38baee5e6bf0bf69e3ca10";
const DEFAULT_AUTHING_APP_HOST = "https://habit.authing.cn";
const SESSION_KEY = "authing_session";
const TOKEN_KEY = "token";
export const AUTHING_CODE_VERIFIER_KEY = "authing_code_verifier";
export const AUTHING_STATE_KEY = "authing_state";

export const authingConfig = {
	appHost: import.meta.env.VITE_AUTHING_APP_HOST || DEFAULT_AUTHING_APP_HOST,
	appId: import.meta.env.VITE_AUTHING_APP_ID || DEFAULT_AUTHING_APP_ID,
	redirectUri:
		import.meta.env.DEV && typeof window !== "undefined"
			? `${window.location.origin}/auth/callback`
			: import.meta.env.VITE_AUTHING_REDIRECT_URI ||
				(typeof window === "undefined" ? "" : `${window.location.origin}/auth/callback`),
};

export const toAuthingSession = (
	user: AuthingUserLike | null | undefined,
	token?: string | null,
): AuthingSession | null => {
	const id = user?.id || user?.sub;
	const sessionToken = token || user?.token || user?.access_token || user?.id_token;
	if (!id || !sessionToken) return null;
	const username = trimValue(user?.username || user?.preferred_username);
	const nickname = trimValue(user?.nickname);
	const rawName = trimValue(user?.name);
	const phone = trimValue(user?.phone_number || user?.phone);
	const email = trimValue(user?.email);
	const displayName = [username, nickname, rawName, phone, email]
		.map((item) => (item ? maskPrivateDisplayName(item) : ""))
		.find(Boolean);

	return {
		avatar: user.picture || user.photo || undefined,
		email: email || undefined,
		id,
		name: displayName || "Authing 用户",
		nickname: nickname || undefined,
		phone: phone || undefined,
		token: sessionToken,
		username: username || undefined,
	};
};

export const saveAuthingSession = (session: AuthingSession) => {
	localStorage.setItem(TOKEN_KEY, session.token);
	localStorage.setItem(SESSION_KEY, JSON.stringify(session));
};

export const loadAuthingSession = (): AuthingSession | null => {
	const cached = localStorage.getItem(SESSION_KEY);
	if (!cached) return null;
	try {
		return JSON.parse(cached) as AuthingSession;
	} catch {
		localStorage.removeItem(SESSION_KEY);
		return null;
	}
};

export const clearAuthingSession = () => {
	localStorage.removeItem(TOKEN_KEY);
	localStorage.removeItem(SESSION_KEY);
	sessionStorage.removeItem(AUTHING_CODE_VERIFIER_KEY);
	sessionStorage.removeItem(AUTHING_STATE_KEY);
};

export const createAuthingState = () => {
	const bytes = new Uint8Array(16);
	crypto.getRandomValues(bytes);
	return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
};
